// CertificateReport.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReportPdfColorModal from "../ReportPdfColorModal";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

import axios from "axios";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import TableViewOutlinedIcon from "@mui/icons-material/TableViewOutlined";

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

import { usePdfSettings, DEFAULT_PDF_COLORS } from "../../../layouts/PdfSettingsContext.js";
import { useBranch } from "../../../layouts/BranchContext.js";

// =========================================================
// PROFESSIONAL DEFAULT PDF THEME
// =========================================================

// =========================================================
// DATE HELPERS
// =========================================================

const pad2 = (value) => String(value).padStart(2, "0");

const formatDateInput = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }

  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
    date.getDate()
  )}`;
};

const getTodayString = () => formatDateInput(new Date());

const getOneMonthAgoString = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return formatDateInput(date);
};

const toDateOnlyString = (value) => {
  if (!value) return "";

  if (value instanceof Date) {
    return formatDateInput(value);
  }

  const raw = String(value).trim();

  if (!raw) return "";

  // YYYY-MM-DD / YYYY-MM-DDTHH:mm:ss
  const isoMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);

  if (isoMatch) {
    return isoMatch[1];
  }

  // DD-MM-YYYY / DD/MM/YYYY
  const dmyMatch = raw.match(
    /^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})/
  );

  if (dmyMatch) {
    const [, day, month, year] = dmyMatch;

    return `${year}-${pad2(month)}-${pad2(day)}`;
  }

  const parsed = new Date(raw);

  if (!Number.isNaN(parsed.getTime())) {
    return formatDateInput(parsed);
  }

  return "";
};

const isDateInRange = (value, fromDate, toDate) => {
  if (!fromDate && !toDate) {
    return true;
  }

  const dateOnly = toDateOnlyString(value);

  // Preserve rows with missing/unparseable dates.
  if (!dateOnly) {
    return true;
  }

  if (fromDate && dateOnly < fromDate) {
    return false;
  }

  if (toDate && dateOnly > toDate) {
    return false;
  }

  return true;
};

// =========================================================
// SAFE TEXT HELPERS
// =========================================================

const safeText = (value, fallback = "-") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }

  if (Array.isArray(value)) {
    const text = value
      .map((item) => safeText(item, ""))
      .filter(Boolean)
      .join(", ");

    return text || fallback;
  }

  if (typeof value === "object") {
    return safeText(
      value?.name ??
        value?.label ??
        value?.title ??
        value?.display_name ??
        value?.branch_name ??
        value?.course_name ??
        value?.batch_name ??
        value?.value ??
        value?.id,
      fallback
    );
  }

  return fallback;
};

const getIdString = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  if (typeof value === "object") {
    return String(value?.id ?? "");
  }

  return String(value);
};

// =========================================================
// TAB CONFIG
// =========================================================

const REPORT_TABS = [
  {
    value: "certificate-approval",
    label: "Certificate Approval",
    title: "Certificate Approval Report",
  },
];

// =========================================================
// COMPONENT
// =========================================================

const CertificateReport = () => {
  const theme = useTheme();

  // =========================================================
  // API CONFIG
  // =========================================================

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL ||
    "https://wims.revisetoexcel.com";

  const CERTIFICATE_REPORT_API =
    `${API_BASE_URL}/api/reports/certificate-approval-report/`;

  const CERTIFICATE_REPORT_EXCEL_API =
    `${API_BASE_URL}/api/reports/reports/certificate-approval-report/export/excel/`;

  const token = localStorage.getItem("accessToken");

  // =========================================================
  // PDF SETTINGS
  // =========================================================

  const {
    pdfThemeColor,
    pdfTextColor,
    updatePdfThemeColor,
    updatePdfTextColor,
  } = usePdfSettings();

  // =========================================================
  // TAB STATE
  // =========================================================

  const [activeTab, setActiveTab] = useState(
    "certificate-approval"
  );

  // =========================================================
  // DATE FILTERS
  // =========================================================

  const [fromDate, setFromDate] = useState(getOneMonthAgoString);
  const [toDate, setToDate] = useState(getTodayString);

  // =========================================================
  // ORGANIZATION / BRANCH
  // =========================================================

  const user = useMemo(() => {
    try {
      return JSON.parse(
        sessionStorage.getItem("user") || "{}"
      );
    } catch {
      return {};
    }
  }, []);

  const {
    selectedBranchId,
    branchList: globalBranchList = [],
    organizationId: globalOrganizationId,
  } = useBranch();

  const organizationId =
    globalOrganizationId ||
    user?.organization_id ||
    user?.organization?.id ||
    "";

  const organizationName =
    user?.organization_name ||
    user?.organizationName ||
    user?.organization?.name ||
    "Organization";

  const [selectedBranchName, setSelectedBranchName] = useState("");

  // =========================================================
  // APPROVAL
  // =========================================================

  const [selectedApprovalId, setSelectedApprovalId] = useState("");
  const [approvalOptions, setApprovalOptions] = useState([]);
  const [loadingApprovals, setLoadingApprovals] = useState(false);

  // =========================================================
  // COURSE
  // =========================================================

  const [courseType, setCourseType] = useState("allCourse");
  const [selectedCourseId, setSelectedCourseId] = useState("");

  const [courseOptions, setCourseOptions] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);

  // =========================================================
  // UI STATE
  // =========================================================

  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const [showColorPickerModal, setShowColorPickerModal] =
    useState(false);

  const [showFilterModal, setShowFilterModal] =
    useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // =========================================================
  // TEMP PDF COLORS
  // =========================================================

  const [tempPdfThemeColor, setTempPdfThemeColor] =
    useState(
      pdfThemeColor || DEFAULT_PDF_COLORS.primary
    );

  const [tempPdfTextColor, setTempPdfTextColor] =
    useState(
      pdfTextColor || DEFAULT_PDF_COLORS.bodyText
    );

  // =========================================================
  // DATE REFS
  // =========================================================

  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);

  // =========================================================
  // USER DATA
  // =========================================================

  const getStoredUser = useCallback(() => {
    try {
      return JSON.parse(
        sessionStorage.getItem("user") || "{}"
      );
    } catch (error) {
      console.warn(
        "Unable to parse stored user:",
        error
      );

      return {};
    }
  }, []);

  // =========================================================
  // DYNAMIC BRANCH DETAILS
  // =========================================================

  const [branchDetails, setBranchDetails] = useState(null);
  const [branchLogo, setBranchLogo] = useState("");

  const getAbsoluteImageUrl = useCallback((imagePath) => {
    if (!imagePath) return "";

    const raw = String(imagePath).trim();

    if (!raw) return "";

    if (/^https?:\/\//i.test(raw)) {
      return raw;
    }

    if (raw.startsWith("data:image/")) {
      return raw;
    }

    const path = raw.startsWith("/") ? raw : `/${raw}`;

    return `https://wims.revisetoexcel.com${path}`;
  }, []);

  const blobToDataUrl = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const convertDataUrlToPng = (dataUrl) =>
    new Promise((resolve, reject) => {
      if (!dataUrl) {
        resolve("");
        return;
      }

      const image = new Image();

      image.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth || image.width || 1;
        canvas.height = image.naturalHeight || image.height || 1;

        const context = canvas.getContext("2d");

        if (!context) {
          resolve(dataUrl);
          return;
        }

        context.drawImage(image, 0, 0);

        try {
          resolve(canvas.toDataURL("image/png"));
        } catch (error) {
          reject(error);
        }
      };

      image.onerror = reject;
      image.src = dataUrl;
    });

  const imageUrlToDataUrl = async (imageUrl) => {
    if (!imageUrl) return "";

    if (String(imageUrl).startsWith("data:image")) {
      try {
        return await convertDataUrlToPng(imageUrl);
      } catch {
        return "";
      }
    }

    try {
      const response = await axios.get(imageUrl, {
        responseType: "blob",
        headers: token
          ? { Authorization: `Bearer ${token}` }
          : {},
      });

      const dataUrl = await blobToDataUrl(response.data);

      return await convertDataUrlToPng(dataUrl);
    } catch (error) {
      console.warn("Dynamic branch logo could not be loaded:", error);
      return "";
    }
  };

  const fetchBranchDetails = useCallback(
    async (branchId) => {
      if (!branchId) {
        setBranchDetails(null);
        setBranchLogo("");
        setSelectedBranchName("");
        return null;
      }

      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/master/branch/${branchId}/`,
          {
            headers: token
              ? { Authorization: `Bearer ${token}` }
              : {},
          }
        );

        const data = response?.data || {};

        const name =
          data?.name ||
          data?.branch_name ||
          data?.branchName ||
          data?.title ||
          "";

        const image =
          data?.image ||
          data?.logo ||
          data?.branch_logo ||
          data?.branchLogo ||
          "";

        const imageUrl = getAbsoluteImageUrl(image);

        setBranchDetails(data);
        setSelectedBranchName(name);
        setBranchLogo(imageUrl);

        return {
          data,
          branchName: name,
          imageUrl,
        };
      } catch (error) {
        console.warn("Failed to load branch details:", error);
        setBranchDetails(null);
        setBranchLogo("");
        setSelectedBranchName("");
        return null;
      }
    },
    [API_BASE_URL, getAbsoluteImageUrl, token]
  );

  useEffect(() => {
    if (selectedBranchId) {
      fetchBranchDetails(selectedBranchId);
    } else {
      setBranchDetails(null);
      setBranchLogo("");
      setSelectedBranchName("");
    }
  }, [selectedBranchId, fetchBranchDetails]);

  useEffect(() => {
    if (!selectedBranchName && selectedBranchId) {
      const branch = globalBranchList.find(
        (item) =>
          String(item?.id ?? item?.branch_id) ===
          String(selectedBranchId)
      );

      if (branch) {
        setSelectedBranchName(
          safeText(
            branch?.name ??
              branch?.branch_name ??
              branch?.branchName,
            ""
          )
        );
      }
    }
  }, [
    globalBranchList,
    selectedBranchId,
    selectedBranchName,
  ]);

  // =========================================================
  // INITIAL GLOBAL CONTEXT SYNC
  // =========================================================

  useEffect(() => {
    if (globalOrganizationId) {
      setErrorMsg(null);
    }
  }, [globalOrganizationId]);

  // =========================================================
  // NORMALIZE APPROVAL
  // =========================================================

  const normalizeApproval = useCallback(
    (item) => {
      const courseBatchDetails = Array.isArray(
        item?.course_batch_details
      )
        ? item.course_batch_details
        : [];

      const courseNames = courseBatchDetails
        .map((detail) =>
          safeText(
            detail?.course_name ??
              detail?.course?.name,
            ""
          )
        )
        .filter(Boolean);

      const batchNames = courseBatchDetails
        .map((detail) =>
          safeText(
            detail?.batch_name ??
              detail?.batch?.name,
            ""
          )
        )
        .filter(Boolean);

      return {
        id: getIdString(item?.id),

        admission_id: getIdString(
          item?.admission_id
        ),

        admission_no: safeText(
          item?.admission_no
        ),

        student_name: safeText(
          item?.student_name
        ),

        mobile_no: safeText(
          item?.mobile_no
        ),

        email: safeText(item?.email),

        organization_name: safeText(
          item?.organization_name ??
            organizationName,
          "Organization"
        ),

        branch_name: safeText(
          item?.branch_name ??
            selectedBranchName,
          "-"
        ),

        total_fee: Number(
          item?.total_fee ?? 0
        ),

        amount_paid: Number(
          item?.amount_paid ?? 0
        ),

        pending_fee: Number(
          item?.pending_fee ?? 0
        ),

        approval_date: safeText(
          item?.approval_date
        ),

        approved_by: safeText(
          item?.approved_by
        ),

        remarks: safeText(
          item?.remarks
        ),

        course_batch_details:
          courseBatchDetails,

        course_name:
          courseNames.length
            ? courseNames.join(", ")
            : "-",

        batch_name:
          batchNames.length
            ? batchNames.join(", ")
            : "-",
      };
    },
    [
      organizationName,
      selectedBranchName,
    ]
  );

  const normalizedApprovalOptions = useMemo(() => {
    return approvalOptions
      .filter(Boolean)
      .map(normalizeApproval);
  }, [
    approvalOptions,
    normalizeApproval,
  ]);

  // =========================================================
  // COURSE / BATCH TEXT
  // =========================================================

  const getCourseBatchText = useCallback(
    (item) => {
      const details = Array.isArray(
        item?.course_batch_details
      )
        ? item.course_batch_details
        : [];

      if (!details.length) {
        return `${safeText(
          item?.course_name
        )} / ${safeText(item?.batch_name)}`;
      }

      return details
        .map((detail) => {
          const courseName = safeText(
            detail?.course_name ??
              detail?.course?.name
          );

          const batchName = safeText(
            detail?.batch_name ??
              detail?.batch?.name
          );

          return `${courseName} / ${batchName}`;
        })
        .join(", ");
    },
    []
  );

  // =========================================================
  // CURRENCY
  // =========================================================

  const formatCurrency = useCallback(
    (value) => {
      const amount = Number(value || 0);

      return `₹${amount.toLocaleString(
        "en-IN",
        {
          maximumFractionDigits: 2,
        }
      )}`;
    },
    []
  );

  // =========================================================
  // COURSE MATCHING
  // =========================================================

  const doesApprovalMatchCourse = useCallback(
    (item) => {
      if (
        courseType !== "selectedCourse" ||
        !selectedCourseId
      ) {
        return true;
      }

      const selectedId =
        String(selectedCourseId);

      const details = Array.isArray(
        item?.course_batch_details
      )
        ? item.course_batch_details
        : [];

      const possibleIds = [
        item?.course_id,
        item?.course?.id,

        ...details.flatMap((detail) => [
          detail?.course_id,
          detail?.course?.id,
          detail?.course?.course_id,
        ]),
      ]
        .map(getIdString)
        .filter(Boolean);

      return possibleIds.includes(
        selectedId
      );
    },
    [
      courseType,
      selectedCourseId,
    ]
  );

  // =========================================================
  // FILTERED DATA
  // =========================================================

  const filteredApprovalOptions = useMemo(() => {
    return normalizedApprovalOptions.filter(
      (item) => {
        const matchesDate =
          isDateInRange(
            item?.approval_date,
            fromDate,
            toDate
          );

        const matchesApproval =
          selectedApprovalId
            ? String(item.id) ===
              String(selectedApprovalId)
            : true;

        const matchesCourse =
          doesApprovalMatchCourse(item);

        return (
          matchesDate &&
          matchesApproval &&
          matchesCourse
        );
      }
    );
  }, [
    normalizedApprovalOptions,
    fromDate,
    toDate,
    selectedApprovalId,
    doesApprovalMatchCourse,
  ]);

  // =========================================================
  // SELECTED APPROVAL
  // =========================================================

  const selectedApproval = useMemo(() => {
    return normalizedApprovalOptions.find(
      (item) =>
        String(item.id) ===
        String(selectedApprovalId)
    );
  }, [
    normalizedApprovalOptions,
    selectedApprovalId,
  ]);

  // =========================================================
  // FETCH CERTIFICATE APPROVAL REPORT
  // POST /api/reports/certificate-approval-report/
  // =========================================================

  const buildReportPayload = useCallback(() => ({
    fromDate: fromDate || "",
    toDate: toDate || "",
    organizationId:
      globalOrganizationId ||
      organizationId ||
      "",
    branch: selectedBranchId || "",
  }), [
    fromDate,
    toDate,
    globalOrganizationId,
    organizationId,
    selectedBranchId,
  ]);

  const fetchApprovals = useCallback(
    async () => {
      const payload = buildReportPayload();

      if (!payload.organizationId || !payload.branch) {
        setApprovalOptions([]);
        setSelectedApprovalId("");
        return;
      }

      setLoadingApprovals(true);
      setErrorMsg(null);

      try {
        const response = await axios.post(
          CERTIFICATE_REPORT_API,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
          }
        );

        const results = Array.isArray(response?.data?.results)
          ? response.data.results
          : [];

        setApprovalOptions(results);
        setPage(0);

        const selectedExists = results.some(
          (item) =>
            String(item?.id ?? "") ===
            String(selectedApprovalId)
        );

        if (selectedApprovalId && !selectedExists) {
          setSelectedApprovalId("");
        }

        return results;
      } catch (error) {
        console.error(
          "Certificate Approval Report API Error:",
          error
        );

        setApprovalOptions([]);
        setSelectedApprovalId("");

        setErrorMsg(
          safeText(
            error?.response?.data?.detail ??
              error?.response?.data?.message ??
              error?.message,
            "Failed to load certificate approval report."
          )
        );

        return [];
      } finally {
        setLoadingApprovals(false);
      }
    },
    [
      buildReportPayload,
      CERTIFICATE_REPORT_API,
      selectedApprovalId,
      token,
    ]
  );

  // =========================================================
  // REPORT EFFECT
  // =========================================================

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  // =========================================================
  // SELECTED BRANCH NAME
  // =========================================================

  useEffect(() => {
    const selectedBranch =
      globalBranchList.find(
        (branch) =>
          String(branch.id) ===
          String(selectedBranchId)
      );

    if (selectedBranch) {
      setSelectedBranchName(
        safeText(selectedBranch.name)
      );
    }
  }, [
    globalBranchList,
    selectedBranchId,
  ]);

  // =========================================================
  // COURSE FETCH
  // =========================================================

  useEffect(() => {
    if (
      courseType !==
        "selectedCourse" ||
      !selectedBranchId
    ) {
      setCourseOptions([]);
      setSelectedCourseId("");

      return;
    }

    const fetchCourses = async () => {
      setLoadingCourses(true);

      try {
        const response =
          await axios.get(
            `${API_BASE_URL}/api/course/courses/`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },

              params: {
                organization:
                  organizationId,

                branch:
                  selectedBranchId,
              },
            }
          );

        const results =
          Array.isArray(response.data)
            ? response.data
            : response.data?.results ||
              [];

        setCourseOptions(
          results.filter(Boolean)
        );
      } catch (error) {
        console.error(
          "Course API Error:",
          error
        );

        setCourseOptions([]);
      } finally {
        setLoadingCourses(false);
      }
    };

    fetchCourses();
  }, [
    API_BASE_URL,
    courseType,
    organizationId,
    selectedBranchId,
    token,
  ]);

  // =========================================================
  // PDF COLOR SYNC
  // =========================================================

  useEffect(() => {
    if (showColorPickerModal) {
      setTempPdfThemeColor(
        pdfThemeColor ||
          DEFAULT_PDF_COLORS.primary
      );

      setTempPdfTextColor(
        pdfTextColor ||
          DEFAULT_PDF_COLORS.bodyText
      );
    }
  }, [
    showColorPickerModal,
    pdfThemeColor,
    pdfTextColor,
  ]);

  // =========================================================
  // RESET
  // =========================================================

  const handleReset = () => {
    setFromDate(
      getOneMonthAgoString()
    );

    setToDate(
      getTodayString()
    );

    setSelectedApprovalId("");

    setCourseType(
      "allCourse"
    );

    setSelectedCourseId("");

    setPage(0);

    setErrorMsg(null);
  };

  // =========================================================
  // HEX TO RGB
  // =========================================================

  const hexToRgb = useCallback(
    (hex) => {
      const safeHex = String(
        hex || "#16355B"
      ).replace("#", "");

      const normalizedHex =
        safeHex.length >= 6
          ? safeHex.slice(0, 6)
          : safeHex.padEnd(
              6,
              "0"
            );

      return [
        parseInt(
          normalizedHex.slice(
            0,
            2
          ),
          16
        ) || 0,

        parseInt(
          normalizedHex.slice(
            2,
            4
          ),
          16
        ) || 0,

        parseInt(
          normalizedHex.slice(
            4,
            6
          ),
          16
        ) || 0,
      ];
    },
    []
  );

  // =========================================================
  // PDF HEADER
  // =========================================================

  const drawPdfHeader = useCallback(
    (
      doc,
      pdfLogo,
      pdfOrganizationName,
      pdfBranchName,
      themeColor,
      generatedAt
    ) => {
      const pageWidth = doc.internal.pageSize.getWidth();
      const primaryColor = hexToRgb(themeColor);
      const margin = 10;

      doc.setFillColor(...primaryColor);
      doc.rect(
        0,
        0,
        pageWidth,
        36,
        "F"
      );

      if (pdfLogo) {
        try {
          doc.addImage(
            pdfLogo,
            "PNG",
            margin,
            5,
            34,
            24
          );
        } catch (error) {
          console.warn("PDF dynamic logo add error:", error);
        }
      }

      doc.setFont("helvetica", "bold");
      doc.setTextColor(...hexToRgb(DEFAULT_PDF_COLORS.headerText));
      doc.setFontSize(16);

      doc.text(
        safeText(pdfOrganizationName, "Organization"),
        pageWidth / 2,
        11,
        { align: "center" }
      );

      doc.setFontSize(11);

      doc.text(
        safeText(pdfBranchName, "Branch"),
        pageWidth / 2,
        18,
        { align: "center" }
      );

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);

      doc.text(
        "Certificate Approval Report",
        pageWidth / 2,
        25,
        { align: "center" }
      );

      doc.setFontSize(7.5);

      doc.text(
        `Generated: ${generatedAt || new Date().toLocaleString()}`,
        pageWidth - margin,
        32,
        { align: "right" }
      );
    },
    [hexToRgb]
  );

  // =========================================================
  // CREATE DYNAMIC PDF
  // =========================================================

  const createCertificatePdf = useCallback(
    async (
      rows,
      themeColor = DEFAULT_PDF_COLORS.primary,
      textColor = DEFAULT_PDF_COLORS.bodyText,
      metadata = {}
    ) => {
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 10;

      let currentBranchName =
        selectedBranchName ||
        metadata?.branch_name ||
        metadata?.branch ||
        "";

      let currentBranchLogo = branchLogo;

      if (selectedBranchId && (!currentBranchName || !currentBranchLogo)) {
        const branchResult = await fetchBranchDetails(selectedBranchId);

        currentBranchName =
          branchResult?.branchName ||
          currentBranchName ||
          "";

        currentBranchLogo =
          branchResult?.imageUrl ||
          currentBranchLogo ||
          "";
      }

      if (!currentBranchName && selectedBranchId) {
        const branch = globalBranchList.find(
          (item) =>
            String(item?.id ?? item?.branch_id) ===
            String(selectedBranchId)
        );

        currentBranchName =
          branch?.name ||
          branch?.branch_name ||
          branch?.branchName ||
          "";
      }

      let pdfLogo = "";

      if (currentBranchLogo) {
        pdfLogo = await imageUrlToDataUrl(currentBranchLogo);
      }

      const currentOrganizationName =
        metadata?.organization_name ||
        metadata?.organization ||
        rows?.find((row) => row?.organization)?.organization ||
        organizationName ||
        "Organization";

      const generatedAt =
        metadata?.generated_at
          ? new Date(metadata.generated_at).toLocaleString()
          : new Date().toLocaleString();

      const drawHeader = () =>
        drawPdfHeader(
          doc,
          pdfLogo,
          currentOrganizationName,
          currentBranchName || "Branch",
          themeColor,
          generatedAt
        );

      drawHeader();

      let y = 43;

      doc.setTextColor(...hexToRgb(textColor));
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text("FILTERS", margin, y);

      y += 5;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);

      const filterText =
        `From Date: ${fromDate || "-"}   |   To Date: ${
          toDate || "-"
        }   |   Organization: ${currentOrganizationName}   |   Branch: ${
          currentBranchName || "-"
        }`;

      doc.text(
        doc.splitTextToSize(
          filterText,
          pageWidth - margin * 2
        ),
        margin,
        y
      );

      y += 9;

      const summaryCount =
        metadata?.summary?.total_certificates_approved ??
        rows.length;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text("SUMMARY", margin, y);

      y += 5;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.text(
        `Total Certificates Approved: ${safeText(
          summaryCount,
          "0"
        )}`,
        margin,
        y
      );

      y += 7;

      const tableRows = rows.map((item, index) => [
        String(index + 1),
        safeText(item?.admission_no),
        safeText(item?.student_name),
        safeText(item?.organization_name),
        safeText(item?.branch_name),
        safeText(item?.approval_date),
        safeText(item?.approved_by),
        safeText(item?.remarks),
      ]);

      const emptyRow = [
        "-",
        "-",
        "No certificate approval data found",
        "-",
        "-",
        "-",
        "-",
        "-",
      ];

      autoTable(doc, {
        startY: y,
        head: [[
          "S.No",
          "Admission No",
          "Student Name",
          "Organization",
          "Branch",
          "Approval Date",
          "Approved By",
          "Remarks",
        ]],
        body: tableRows.length ? tableRows : [emptyRow],
        theme: "grid",
        margin: {
          top: 42,
          left: margin,
          right: margin,
          bottom: 14,
        },
        styles: {
          font: "helvetica",
          fontSize: 7.5,
          textColor: hexToRgb(textColor),
          cellPadding: 2,
          valign: "middle",
          overflow: "linebreak",
        },
        headStyles: {
          fillColor: hexToRgb(themeColor),
          textColor: hexToRgb(DEFAULT_PDF_COLORS.tableHeaderText),
          fontStyle: "bold",
          fontSize: 8,
          halign: "center",
          valign: "middle",
        },
        alternateRowStyles: {
          fillColor: hexToRgb(DEFAULT_PDF_COLORS.alternateRow),
        },
        columnStyles: {
          0: { cellWidth: 11, halign: "center" },
          1: { cellWidth: 30 },
          2: { cellWidth: 38 },
          3: { cellWidth: 40 },
          4: { cellWidth: 32 },
          5: { cellWidth: 27, halign: "center" },
          6: { cellWidth: 32 },
          7: { cellWidth: 50 },
        },
        willDrawPage: () => {
          drawHeader();
        },
        didDrawPage: () => {
          const currentPage =
            doc.internal.getNumberOfPages();

          doc.setFont("helvetica", "normal");
          doc.setFontSize(7);
          doc.setTextColor(...hexToRgb(textColor));

          doc.text(
            "Certificate Approval Report",
            margin,
            pageHeight - 5
          );

          doc.text(
            `Page ${currentPage}`,
            pageWidth - margin,
            pageHeight - 5,
            { align: "right" }
          );
        },
      });

      return doc;
    },
    [
      branchLogo,
      drawPdfHeader,
      fetchBranchDetails,
      fromDate,
      globalBranchList,
      hexToRgb,
      imageUrlToDataUrl,
      organizationName,
      selectedBranchId,
      selectedBranchName,
      toDate,
    ]
  );

  // =========================================================
  // GENERATE PDF FROM ALREADY LOADED REPORT DATA
  // No report API call here.
  // =========================================================

  const generatePDF = async (
    themeColor = tempPdfThemeColor,
    textColor = tempPdfTextColor,
    reportWindow = null
  ) => {
    setPdfLoading(true);
    setErrorMsg(null);

    try {
      const reportRows = filteredApprovalOptions;

      if (!reportRows.length) {
        throw new Error(
          "No certificate approval data available for the selected filters."
        );
      }

      const doc = await createCertificatePdf(
        reportRows,
        themeColor || DEFAULT_PDF_COLORS.primary,
        textColor || DEFAULT_PDF_COLORS.bodyText,
        {
          summary: {
            total_certificates_approved:
              reportRows.length,
          },
          organization_name: organizationName,
          branch_name: selectedBranchName,
          generated_at: new Date().toISOString(),
        }
      );

      const pdfBlob = doc.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);

      if (reportWindow && !reportWindow.closed) {
        reportWindow.location.href = pdfUrl;
      } else {
        const newWindow = window.open(
          pdfUrl,
          "_blank"
        );

        if (!newWindow) {
          const link = document.createElement("a");

          link.href = pdfUrl;
          link.download = `Certificate_Approval_Report_${fromDate}_to_${toDate}.pdf`;

          document.body.appendChild(link);
          link.click();
          link.remove();
        }
      }

      setTimeout(() => {
        URL.revokeObjectURL(pdfUrl);
      }, 60000);
    } catch (error) {
      console.error(
        "Generate Certificate PDF Error:",
        error
      );

      if (
        reportWindow &&
        !reportWindow.closed
      ) {
        reportWindow.close();
      }

      setErrorMsg(
        safeText(
          error?.response?.data?.detail ??
            error?.response?.data?.message ??
            error?.message,
          "Failed to generate certificate PDF."
        )
      );
    } finally {
      setPdfLoading(false);
    }
  };

  // =========================================================
  // GENERATE REPORT
  // REPORT API -> TABLE -> PDF COLOR PICKER
  // =========================================================

  const handleGenerateReport = async () => {
    const refreshedRows =
      (await fetchApprovals()) || [];

    if (!Array.isArray(refreshedRows) || refreshedRows.length === 0) {
      setShowColorPickerModal(false);

      setErrorMsg(
        "No certificate approval data found for the selected filters."
      );

      return;
    }

    setTempPdfThemeColor(
      pdfThemeColor ||
        DEFAULT_PDF_COLORS.primary
    );

    setTempPdfTextColor(
      pdfTextColor ||
        DEFAULT_PDF_COLORS.bodyText
    );

    setShowColorPickerModal(true);
  };

  // =========================================================
  // EXPORT EXCEL API
  // =========================================================

  const handleCertificateDownloadExcel = async () => {
    setLoading(true);
    setErrorMsg(null);

    try {
      const payload = buildReportPayload();

      if (!payload.organizationId || !payload.branch) {
        throw new Error(
          "Organization and global branch are required."
        );
      }

      const response = await axios.post(
        CERTIFICATE_REPORT_EXCEL_API,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "*/*",
          },
          responseType: "blob",
        }
      );

      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement("a");

      link.href = blobUrl;
      link.download = `Certificate_Approval_Report_${fromDate}_to_${toDate}.xlsx`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
      console.error("Certificate Excel export error:", error);

      setErrorMsg(
        safeText(
          error?.response?.data?.detail ??
            error?.response?.data?.message ??
            error?.message,
          "Failed to export Certificate Approval Excel report."
        )
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // PAGINATION
  // =========================================================

  useEffect(() => {
    const maxPage =
      Math.max(
        0,
        Math.ceil(
          filteredApprovalOptions.length /
            rowsPerPage
        ) - 1
      );

    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [
    filteredApprovalOptions.length,
    page,
    rowsPerPage,
  ]);

  const paginatedRows =
    filteredApprovalOptions.slice(
      page * rowsPerPage,
      page * rowsPerPage +
        rowsPerPage
    );

  // =========================================================
  // DATE FIELD STYLES
  // =========================================================

  const dateFieldSx = {
    "& .MuiInputLabel-root": {
      color:
        theme.palette.text.primary,
    },

    "& .MuiInputLabel-root.Mui-focused":
      {
        color:
          theme.palette.primary.main,
      },

    "& .MuiOutlinedInput-notchedOutline":
      {
        borderColor:
          theme.palette.divider,
      },

    "&:hover .MuiOutlinedInput-notchedOutline":
      {
        borderColor:
          theme.palette.primary.main,
      },

    "& .MuiInputBase-root": {
      cursor: "pointer",
    },

    "& .MuiInputBase-input": {
      cursor: "pointer",
    },
  };

  // =========================================================
  // FILTER COUNT
  // =========================================================

  const activeFilterCount = [
    selectedBranchId,
    selectedApprovalId,
    fromDate,
    toDate,
    courseType ===
      "selectedCourse"
      ? selectedCourseId
      : "",
  ].filter(
    (value) =>
      value !== "" &&
      value !== null &&
      value !== undefined
  ).length;

  // =========================================================
  // CURRENT TAB
  // =========================================================

  const currentTab =
    REPORT_TABS.find(
      (tab) =>
        tab.value === activeTab
    ) || REPORT_TABS[0];

  // =========================================================
  // COMMON BUTTON SX
  // =========================================================

  const actionButtonSx = {
    textTransform: "none",
    fontWeight: 700,
    borderRadius: 2,
    minHeight: 40,
  };

  // =========================================================
  // UI
  // =========================================================

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: {
          xs: 1.5,
          md: 2.5,
        },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          background: "unset",
          backgroundColor: "unset",
          boxShadow: "none",
        }}
      >
        {/* =====================================================
            TAB BAR
        ====================================================== */}
<Box
  sx={{
    mb: 2,
    p: 0.5,
    borderRadius: 2,
    borderBottom: "1px solid",
    borderColor: "divider",
    overflowX: "auto",
  }}
>
  <Tabs
    value={activeTab}
    onChange={(_, value) => {
      setActiveTab(value);
      setPage(0);
      setErrorMsg(null);
    }}
    variant="scrollable"
    scrollButtons="auto"
    allowScrollButtonsMobile
    sx={{
      minHeight: 42,

      "& .MuiTabs-indicator": {
        display: "none",
      },

      "& .MuiTab-root": {
        minHeight: 42,
        minWidth: {
          xs: 150,
          sm: 180,
        },
        px: {
          xs: 1.5,
          md: 2,
        },
        borderRadius: 1.5,
        textTransform: "none",
        fontWeight: 600,
        fontSize: {
          xs: 13,
          sm: 14,
        },
        color: "text.secondary",
        opacity: 1,
        transition: "all 0.2s ease",

        "&:hover": {
          backgroundColor: "action.hover",
          color: "text.primary",
        },
      },

      "& .MuiTab-root.Mui-selected": {
        backgroundColor: "primary.main",
        color: "primary.contrastText",

        "&:hover": {
          backgroundColor: "primary.dark",
        },
      },
    }}
  >
    {REPORT_TABS.map((tab) => (
      <Tab
        key={tab.value}
        value={tab.value}
        label={tab.label}
        icon={
          <AssessmentOutlinedIcon
            sx={{
              fontSize: 20,
            }}
          />
        }
        iconPosition="start"
      />
    ))}
  </Tabs>
</Box>
        {/* =====================================================
            REPORT HEADER
        ====================================================== */}

        <Box
          sx={{
            px: {
              xs: 2,
              md: 3,
            },

            py: {
              xs: 2,
              md: 2.5,
            },

            background: "unset",
            backgroundColor: "unset",
          }}
        >
          <Stack
            direction={{
              xs: "column",
              lg: "row",
            }}
            justifyContent="space-between"
            alignItems={{
              xs: "stretch",
              lg: "center",
            }}
            spacing={2}
          >
            {/* TITLE */}

            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
            >
   
            </Stack>

            {/* ACTIONS */}

            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              spacing={1}
              flexWrap="wrap"
              useFlexGap
            >
              <Button
                variant="contained"
                startIcon={
                  <FilterAltOutlinedIcon />
                }
                onClick={() =>
                  setShowFilterModal(
                    true
                  )
                }
                sx={{
                  ...actionButtonSx,
                  minWidth: {
                    sm: 120,
                  },
                }}
              >
                Filters
                {activeFilterCount >
                  0 && (
                  <Chip
                    size="small"
                    label={
                      activeFilterCount
                    }
                    sx={{
                      ml: 0.75,
                      height: 21,
                      fontSize: 11,
                    }}
                  />
                )}
              </Button>

              <Button
                variant="contained"
                startIcon={
                  loading ? (
                    <CircularProgress
                      size={17}
                      color="inherit"
                    />
                  ) : (
                    <AssessmentOutlinedIcon />
                  )
                }
                onClick={handleGenerateReport}
                disabled={loading}
                sx={{
                  ...actionButtonSx,
                  minWidth: {
                    sm: 155,
                  },
                }}
              >
                Generate Report
              </Button>

           

              <Button
                variant="contained"
                  color="success"
                startIcon={
                  <TableViewOutlinedIcon />
                }
                onClick={
                  handleCertificateDownloadExcel
                }
                disabled={
                  loading ||
                  filteredApprovalOptions.length ===
                    0
                }
                sx={{
                  ...actionButtonSx,
                  minWidth: {
                    sm: 135,
                  },
                }}
              >
                Excel
              </Button>

              <Button
                variant="contained"
                startIcon={
                  <RefreshOutlinedIcon />
                }
                onClick={
                  handleReset
                }
                sx={{
                  ...actionButtonSx,
                }}
              >
                Reset
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* =====================================================
            REPORT CONTENT
        ====================================================== */}

        <Box
          sx={{
            p: {
              xs: 1.5,
              sm: 2,
              md: 3,
            },
          }}
        >
          {/* ERROR */}

          {errorMsg && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: 2,
              }}
              onClose={() =>
                setErrorMsg(null)
              }
            >
              {safeText(
                errorMsg
              )}
            </Alert>
          )}

          {/* REPORT SUMMARY */}

          <Paper
            variant="contained"
            sx={{
              borderRadius: 2.5,
              overflow: "hidden",
              borderColor:
                theme.palette.divider,
              backgroundColor: "transparent",
            }}
          >
            <Box
              sx={{
                px: {
                  xs: 1.5,
                  md: 2,
                },

                py: 1.5,

                display: "flex",

                alignItems: {
                  xs: "flex-start",
                  md: "center",
                },

                justifyContent:
                  "space-between",

                gap: 2,

                flexDirection: {
                  xs: "column",
                  md: "row",
                },

                background: "transparent",
                backgroundColor: "transparent",
              }}
            >
         
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
              >
                <Chip
                  size="small"
                  color="primary"
                  variant="contained"
                  label={`${filteredApprovalOptions.length} Approval${
                    filteredApprovalOptions.length ===
                    1
                      ? ""
                      : "s"
                  }`}
                />

                {selectedApproval &&
                  filteredApprovalOptions.length >
                    0 && (
                    <Chip
                      size="small"
                      label={safeText(
                        selectedApproval.admission_no
                      )}
                    />
                  )}
              </Stack>
            </Box>

            <Divider />

            {/* =================================================
                TABLE
            ================================================== */}

            <TableContainer
              sx={{
                backgroundColor:
                  theme.palette.background.paper,
                maxHeight: {
                  xs: "calc(100vh - 330px)",
                  md: 620,
                },

                overflowX: "auto",

                "&::-webkit-scrollbar":
                  {
                    height: 7,
                    width: 7,
                  },

                "&::-webkit-scrollbar-thumb":
                  {
                    backgroundColor:
                      theme.palette.mode ===
                      "dark"
                        ? "rgba(255,255,255,0.2)"
                        : "rgba(0,0,0,0.2)",

                    borderRadius: 5,
                  },
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 1000,

                  "& .MuiTableCell-root":
                    {
                      borderColor:
                        theme.palette.divider,
                    },

                  "& .MuiTableBody-root .MuiTableRow-root": {
                    backgroundColor:
                      theme.palette.background.paper,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {[
                      "S.No",
                      "Admission No",
                      "Student Name",
                      "Organization",
                      "Branch",
                      "Approval Date",
                      "Approved By",
                      "Remarks",
                    ].map(
                      (header) => (
                        <TableCell
                          key={header}
                          sx={{
                            fontWeight: 800,

                            backgroundColor:
                              theme.palette
                                .primary
                                .main,

                            color:
                              theme.palette
                                .primary
                                .contrastText,

                            whiteSpace:
                              "nowrap",

                            borderBottom:
                              "none",

                            py: 1.5,
                          }}
                        >
                          {header}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loadingApprovals ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        align="center"
                        sx={{
                          py: 8,
                        }}
                      >
                        <Stack
                          alignItems="center"
                          spacing={1.5}
                        >
                          <CircularProgress
                            size={30}
                          />

                          <Typography
                            variant="body2"
                            color="text.secondary"
                          >
                            Loading approval
                            data...
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ) : paginatedRows.length ===
                    0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        align="center"
                        sx={{
                          py: 8,
                        }}
                      >
                        <Stack
                          alignItems="center"
                          spacing={1}
                        >
                          <AssessmentOutlinedIcon
                            sx={{
                              fontSize: 42,
                              color:
                                theme.palette
                                  .text
                                  .disabled,
                            }}
                          />

                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 700,
                            }}
                          >
                            No approval data
                            found
                          </Typography>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                          >
                            Try changing the
                            selected filters
                            or date range.
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedRows.map(
                      (
                        item,
                        index
                      ) => {
                        const rowIndex =
                          page *
                            rowsPerPage +
                          index;

                        const isSelected =
                          String(
                            selectedApprovalId
                          ) ===
                          String(
                            item.id
                          );

                        return (
                          <TableRow
                            key={
                              item.id ||
                              `${safeText(
                                item.admission_no
                              )}-${rowIndex}`
                            }
                            hover
                            selected={
                              isSelected
                            }
                            onClick={() => {
                              setSelectedApprovalId(
                                String(
                                  item.id
                                )
                              );
                            }}
                            sx={{
                              cursor:
                                "pointer",

                              // Actual data rows must stay solid/contained.
                              backgroundColor:
                                isSelected
                                  ? theme.palette.action.selected
                                  : theme.palette.background.paper,

                              "&:nth-of-type(odd)":
                                {
                                  backgroundColor:
                                    theme.palette.background.paper,
                                },

                              "&:nth-of-type(even)":
                                {
                                  backgroundColor:
                                    theme.palette.background.paper,
                                },

                              "&:hover":
                                {
                                  backgroundColor:
                                    theme.palette.action.hover,
                                },

                              "&.Mui-selected":
                                {
                                  backgroundColor:
                                    theme.palette.action.selected,

                                  "&:hover":
                                    {
                                      backgroundColor:
                                        theme.palette.action.selected,
                                    },
                                },

                              "& .MuiTableCell-root":
                                {
                                  py: 1.35,
                                  whiteSpace:
                                    "nowrap",
                                  backgroundColor:
                                    isSelected
                                      ? theme.palette.action.selected
                                      : theme.palette.background.paper,
                                },

                              "&:hover .MuiTableCell-root": {
                                backgroundColor: isSelected
                                  ? theme.palette.action.selected
                                  : theme.palette.action.hover,
                                },
                            }}
                          >
                            <TableCell>
                              {rowIndex +
                                1}
                            </TableCell>

                            <TableCell>
                              {safeText(
                                item.admission_no
                              )}
                            </TableCell>

                            <TableCell
                              sx={{
                                fontWeight: 600,
                                minWidth: 180,
                              }}
                            >
                              {safeText(
                                item.student_name
                              )}
                            </TableCell>

                            <TableCell>
                              {safeText(
                                item.organization
                              )}
                            </TableCell>

                            <TableCell>
                              {safeText(
                                item.branch
                              )}
                            </TableCell>

                            <TableCell>
                              {safeText(
                                item.approval_date
                              )}
                            </TableCell>

                            <TableCell>
                              {safeText(
                                item.approved_by
                              )}
                            </TableCell>

                            <TableCell
                              sx={{
                                maxWidth: 280,
                                whiteSpace:
                                  "normal !important",
                                minWidth: 220,
                              }}
                            >
                              {safeText(
                                item.remarks
                              )}
                            </TableCell>

                          </TableRow>
                        );
                      }
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* PAGINATION */}

            {filteredApprovalOptions.length >
              0 && (
              <Box
                sx={{
                  borderTop: `1px solid ${theme.palette.divider}`,
                }}
              >
                <TablePagination
                  component="div"
                  count={
                    filteredApprovalOptions.length
                  }
                  page={page}
                  onPageChange={(
                    _,
                    newPage
                  ) =>
                    setPage(
                      newPage
                    )
                  }
                  rowsPerPage={
                    rowsPerPage
                  }
                  onRowsPerPageChange={(
                    event
                  ) => {
                    setRowsPerPage(
                      parseInt(
                        event.target
                          .value,
                        10
                      )
                    );

                    setPage(0);
                  }}
                  rowsPerPageOptions={[
                    10,
                    25,
                    50,
                    100,
                  ]}
                />
              </Box>
            )}
          </Paper>
        </Box>
      </Paper>

      {/* =======================================================
          FILTER MODAL
      ======================================================== */}

      <Modal
        open={showFilterModal}
        onClose={() =>
          setShowFilterModal(
            false
          )
        }
        aria-labelledby="certificate-filter-modal"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform:
              "translate(-50%, -50%)",

            width: {
              xs: "94vw",
              sm: 700,
            },

            maxHeight: "90vh",
            overflowY: "auto",

            bgcolor:
              "background.paper",

            borderRadius: 3,

            border: `1px solid ${theme.palette.divider}`,

            boxShadow: 24,

            outline: "none",
          }}
        >
          {/* MODAL HEADER */}

          <Box
            sx={{
              px: {
                xs: 2,
                md: 2.5,
              },

              py: 1.75,

              display: "flex",

              justifyContent:
                "space-between",

              alignItems:
                "center",

              backgroundColor:
                theme.palette.primary
                  .main,

              color:
                theme.palette.primary
                  .contrastText,
              position: "sticky",
              top: 0,
              zIndex: 3,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                }}
              >
                Report Filters
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  opacity: 0.85,
                }}
              >
                Select branch, approval,
                course and date range.
              </Typography>
            </Box>

            <IconButton
              onClick={() =>
                setShowFilterModal(
                  false
                )
              }
              sx={{
                color:
                  "inherit",
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* MODAL BODY */}

          <Box
            sx={{
              p: {
                xs: 2,
                md: 2.5,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                width: "100%",
              }}
            >
              {/* BRANCH */}

                            {/* APPROVAL */}

              <Box
                sx={{
                  width: {
                    xs: "100%",
                    sm: "calc(50% - 8px)",
                  },
                }}
              >
                <FormControl
                  fullWidth
                  size="small"
                >
                  <InputLabel>
                    Approval
                  </InputLabel>

                  <Select
                    value={String(
                      selectedApprovalId ||
                        ""
                    )}
                    label="Approval"
                    onChange={(
                      event
                    ) => {
                      setSelectedApprovalId(
                        String(
                          event
                            .target
                            .value ||
                            ""
                        )
                      );

                      setPage(0);
                    }}
                  >
                    <MenuItem value="">
                      <em>
                        All Approvals
                      </em>
                    </MenuItem>

                    {loadingApprovals ? (
                      <MenuItem disabled>
                        Loading...
                      </MenuItem>
                    ) : normalizedApprovalOptions.length >
                      0 ? (
                      normalizedApprovalOptions.map(
                        (item) => (
                          <MenuItem
                            key={
                              item.id
                            }
                            value={String(
                              item.id
                            )}
                          >
                            {safeText(
                              item.student_name
                            )}{" "}
                            (
                            {safeText(
                              item.admission_no
                            )}
                            )
                          </MenuItem>
                        )
                      )
                    ) : (
                      <MenuItem disabled>
                        No Approval
                        Found
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Box>

              {/* FROM DATE */}

              <Box
                sx={{
                  width: {
                    xs: "100%",
                    sm: "calc(50% - 8px)",
                  },
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="From Date"
                  type="date"
                  value={fromDate}
                  onChange={(
                    event
                  ) => {
                    const value =
                      event.target
                        .value;

                    setFromDate(
                      value
                    );

                    setPage(0);

                    if (
                      toDate &&
                      value >
                        toDate
                    ) {
                      setToDate(
                        value
                      );
                    }
                  }}
                  inputRef={
                    fromDateRef
                  }
                  onClick={() =>
                    fromDateRef.current?.showPicker?.()
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    max:
                      toDate ||
                      undefined,
                  }}
                  sx={
                    dateFieldSx
                  }
                />
              </Box>

              {/* TO DATE */}

              <Box
                sx={{
                  width: {
                    xs: "100%",
                    sm: "calc(50% - 8px)",
                  },
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="To Date"
                  type="date"
                  value={toDate}
                  onChange={(
                    event
                  ) => {
                    const value =
                      event.target
                        .value;

                    setToDate(
                      value
                    );

                    setPage(0);

                    if (
                      fromDate &&
                      value <
                        fromDate
                    ) {
                      setFromDate(
                        value
                      );
                    }
                  }}
                  inputRef={
                    toDateRef
                  }
                  onClick={() =>
                    toDateRef.current?.showPicker?.()
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min:
                      fromDate ||
                      undefined,
                  }}
                  sx={
                    dateFieldSx
                  }
                />
              </Box>

              {/* COURSE FILTER */}

              <Box
                sx={{
                  width: "100%",
                }}
              >
                <FormControl
                  fullWidth
                  size="small"
                >
                  <InputLabel>
                    Course Filter
                  </InputLabel>

                  <Select
                    value={
                      courseType
                    }
                    label="Course Filter"
                    onChange={(
                      event
                    ) => {
                      const value =
                        event
                          .target
                          .value;

                      setCourseType(
                        value
                      );

                      if (
                        value !==
                        "selectedCourse"
                      ) {
                        setSelectedCourseId(
                          ""
                        );
                      }

                      setPage(0);
                    }}
                  >
                    <MenuItem value="allCourse">
                      All Courses
                    </MenuItem>

                    <MenuItem value="selectedCourse">
                      Selected Course
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* COURSE */}

              {courseType ===
                "selectedCourse" && (
                <Box
                  sx={{
                    width: "100%",
                  }}
                >
                  <FormControl
                    fullWidth
                    size="small"
                  >
                    <InputLabel>
                      Course
                    </InputLabel>

                    <Select
                      value={String(
                        selectedCourseId ||
                          ""
                      )}
                      label="Course"
                      onChange={(
                        event
                      ) => {
                        setSelectedCourseId(
                          String(
                            event
                              .target
                              .value ||
                              ""
                          )
                        );

                        setPage(0);
                      }}
                    >
                      <MenuItem value="">
                        <em>
                          All Courses
                        </em>
                      </MenuItem>

                      {loadingCourses ? (
                        <MenuItem disabled>
                          Loading...
                        </MenuItem>
                      ) : courseOptions.length >
                        0 ? (
                        courseOptions.map(
                          (
                            course
                          ) => {
                            const courseId =
                              getIdString(
                                course?.id ??
                                  course?.course_id
                              );

                            if (
                              !courseId
                            ) {
                              return null;
                            }

                            return (
                              <MenuItem
                                key={
                                  courseId
                                }
                                value={
                                  courseId
                                }
                              >
                                {safeText(
                                  course?.name ??
                                    course?.course_name,
                                  "-"
                                )}
                              </MenuItem>
                            );
                          }
                        )
                      ) : (
                        <MenuItem disabled>
                          No Course
                          Found
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Box>
              )}
            </Box>

            <Divider
              sx={{
                my: 2.5,
              }}
            />

            {/* FILTER FOOTER */}

            <Stack
              direction={{
                xs: "column-reverse",
                sm: "row",
              }}
              justifyContent="flex-end"
              spacing={1}
              sx={{
                position: "sticky",
                bottom: 0,
                zIndex: 3,
                py: 1.5,
                backgroundColor: "background.paper",
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Button
                variant="contained"
                startIcon={
                  <RefreshOutlinedIcon />
                }
                onClick={
                  handleReset
                }
                sx={{
                  ...actionButtonSx,
                }}
              >
                Reset
              </Button>

              <Button
                variant="contained"
                onClick={() => {
                  setPage(0);
                  setShowFilterModal(
                    false
                  );
                }}
                sx={{
                  ...actionButtonSx,
                }}
              >
                Apply Filters
              </Button>
            </Stack>
          </Box>
        </Box>
      </Modal>

      {/* =======================================================
          PDF COLOR MODAL
      ======================================================== */}

      <ReportPdfColorModal
  open={showColorPickerModal}
  onClose={() =>
          setShowColorPickerModal(
            false
          )}
  themeColor={tempPdfThemeColor}
  setThemeColor={(hex) => {
                      setTempPdfThemeColor(
                        hex
                      );

                      updatePdfThemeColor?.(
                        hex
                      );
                     }}
  textColor={tempPdfTextColor}
  setTextColor={(hex) => {
                      setTempPdfTextColor(
                        hex
                      );

                      updatePdfTextColor?.(
                        hex
                      );
                     }}
  reportTitle="Certificate Report"
  onGenerate={async () => {
                  setShowColorPickerModal(false);

                  const reportWindow =
                    window.open(
                      "",
                      "_blank"
                    );

                  if (reportWindow) {
                    reportWindow.document.write(`
                      <html>
                        <head>
                          <title>Generating Certificate Approval PDF...</title>
                        </head>
                        <body style="margin:0;display:flex;align-items:center;justify-content:center;height:100vh;font-family:Arial,sans-serif;">
                          <div>Generating Certificate Approval PDF...</div>
                        </body>
                      </html>
                    `);
                    reportWindow.document.close();
                  }

                  await generatePDF(
                    tempPdfThemeColor,
                    tempPdfTextColor,
                    reportWindow
                  );
                }}
  loading={loading || pdfLoading}
  disabled={filteredApprovalOptions.length ===
                    0 ||
                  loading ||
                  pdfLoading}
/>
    </Container>
  );
};

export default CertificateReport;